import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import StaticPageLayout from '../../components/StaticPageLayout';
import { useThemeStore } from '../../store/theme';

const automationsOverviewLinks = [
  { title: 'Automations Explained', href: '#automations-explained' },
  { title: 'How automations work in SweetPilot', href: '#how-automations-work' },
  { title: 'Setting up your first automation', href: '#first-automation' },
  { title: 'Best practices for automations', href: '#automation-best-practices' },
];

const inboxManagerLinks = [
  { title: 'Inbox Manager automation explained', href: '#inbox-manager-explained' },
  { title: 'Setting up automatic email responses', href: '#auto-email-responses' },
  { title: 'Email categorization and prioritization', href: '#email-categorization' },
  { title: 'Smart email forwarding rules', href: '#email-forwarding' },
  { title: 'Email summary and digest automation', href: '#email-digest' },
];

const socialMediaLinks = [
  { title: 'Social media content generation automation', href: '#social-content-generation' },
  { title: 'Scheduling posts across platforms', href: '#schedule-posts' },
  { title: 'Auto-generating captions and hashtags', href: '#auto-captions' },
  { title: 'Content calendar automation', href: '#content-calendar' },
  { title: 'Engagement tracking and reporting', href: '#engagement-tracking' },
];

const socialMediaManagerLinks = [
  { title: 'Social Media Manager automation FAQ', href: '#social-manager-faq' },
  { title: 'Connecting multiple social accounts', href: '#connect-social-accounts' },
  { title: 'Cross-platform posting automation', href: '#cross-platform-posting' },
  { title: 'Analytics and performance reports', href: '#analytics-reports' },
  { title: 'Managing comments and messages', href: '#manage-comments' },
];

const taskAutomationLinks = [
  { title: 'Task creation and assignment automation', href: '#task-automation' },
  { title: 'Recurring task scheduling', href: '#recurring-tasks' },
  { title: 'Task reminders and notifications', href: '#task-reminders' },
  { title: 'Project milestone tracking', href: '#milestone-tracking' },
  { title: 'Team workload balancing', href: '#workload-balancing' },
];

const workflowLinks = [
  { title: 'Creating custom workflows', href: '#custom-workflows' },
  { title: 'Trigger-based automations', href: '#trigger-automations' },
  { title: 'Multi-step workflow chains', href: '#workflow-chains' },
  { title: 'Conditional logic in automations', href: '#conditional-logic' },
  { title: 'Automation templates', href: '#automation-templates' },
];

const integrationAutomationLinks = [
  { title: 'Automating with Slack integration', href: '#slack-automation' },
  { title: 'Automating with Trello integration', href: '#trello-automation' },
  { title: 'Automating with Google Calendar', href: '#calendar-automation' },
  { title: 'Automating with email services', href: '#email-automation' },
  { title: 'Connecting multiple integrations in workflows', href: '#multi-integration' },
];

const troubleshootingLinks = [
  { title: 'Automation not triggering', href: '#automation-not-triggering' },
  { title: 'Delayed automation execution', href: '#delayed-execution' },
  { title: 'Failed automation runs', href: '#failed-runs' },
  { title: 'Viewing automation logs', href: '#automation-logs' },
  { title: 'Automation limits and quotas', href: '#limits-quotas' },
];

interface LinkItemProps {
  title: string;
  href: string;
  isDark: boolean;
}

function LinkItem({ title, href, isDark }: LinkItemProps) {
  return (
    <Link
      to={href}
      className={`flex items-center justify-between py-4 px-2 transition-all group border-b ${
        isDark
          ? 'border-white/5 hover:bg-white/5'
          : 'border-slate-100 hover:bg-slate-50'
      }`}
    >
      <span className={`font-medium transition-colors ${
        isDark ? 'text-white group-hover:text-purple-300' : 'text-slate-900 group-hover:text-purple-600'
      }`}>
        {title}
      </span>
      <ChevronRight className={`w-5 h-5 transition-colors ${
        isDark ? 'text-gray-600 group-hover:text-purple-400' : 'text-slate-300 group-hover:text-purple-600'
      }`} />
    </Link>
  );
}

interface SectionCardProps {
  title: string;
  links: Array<{ title: string; href: string }>;
  isDark: boolean;
  delay?: number;
}

function SectionCard({ title, links, isDark, delay = 0 }: SectionCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className={`rounded-2xl border overflow-hidden ${
        isDark
          ? 'bg-dark-800/50 border-white/5'
          : 'bg-white border-slate-200 shadow-sm'
      }`}
    >
      <div className={`px-6 py-5 border-b ${isDark ? 'border-white/5' : 'border-slate-100'}`}>
        <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{title}</h2>
      </div>
      <div className="px-4">
        {links.map((link) => (
          <LinkItem
            key={link.title + link.href}
            title={link.title}
            href={link.href}
            isDark={isDark}
          />
        ))}
      </div>
    </motion.div>
  );
}

export default function AutomationsDocs() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  return (
    <StaticPageLayout
      title=""
      subtitle=""
      showBackButton={false}
    >
      {/* Breadcrumbs */}
      <nav className="mb-8">
        <ol className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
          <li>
            <Link
              to="/docs"
              className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}
            >
              All Collections
            </Link>
          </li>
          <li>
            <ChevronRight className="w-4 h-4" />
          </li>
          <li className={isDark ? 'text-white' : 'text-slate-900'}>
            Automations
          </li>
        </ol>
      </nav>

      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-10"
      >
        <h1 className={`text-4xl font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
          Automations
        </h1>
        <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
          Set up automated workflows to save time and boost productivity.
        </p>
      </motion.div>

      {/* Automations Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className={`rounded-2xl border overflow-hidden mb-6 ${
          isDark
            ? 'bg-dark-800/50 border-white/5'
            : 'bg-white border-slate-200 shadow-sm'
        }`}
      >
        <div className="px-4">
          {automationsOverviewLinks.map((link) => (
            <LinkItem
              key={link.title}
              title={link.title}
              href={link.href}
              isDark={isDark}
            />
          ))}
        </div>
      </motion.div>

      {/* Inbox Manager */}
      <SectionCard
        title="Inbox Manager"
        links={inboxManagerLinks}
        isDark={isDark}
        delay={0.2}
      />

      <div className="h-6" />

      {/* Social Media Content Generation */}
      <SectionCard
        title="Social Media Content Generation"
        links={socialMediaLinks}
        isDark={isDark}
        delay={0.3}
      />

      <div className="h-6" />

      {/* Social Media Manager */}
      <SectionCard
        title="Social Media Manager"
        links={socialMediaManagerLinks}
        isDark={isDark}
        delay={0.4}
      />

      <div className="h-6" />

      {/* Task Automation */}
      <SectionCard
        title="Task Automation"
        links={taskAutomationLinks}
        isDark={isDark}
        delay={0.5}
      />

      <div className="h-6" />

      {/* Custom Workflows */}
      <SectionCard
        title="Custom Workflows"
        links={workflowLinks}
        isDark={isDark}
        delay={0.6}
      />

      <div className="h-6" />

      {/* Integration Automations */}
      <SectionCard
        title="Integration Automations"
        links={integrationAutomationLinks}
        isDark={isDark}
        delay={0.7}
      />

      <div className="h-6" />

      {/* Troubleshooting */}
      <SectionCard
        title="Troubleshooting Automations"
        links={troubleshootingLinks}
        isDark={isDark}
        delay={0.8}
      />

      <div className="h-16" />

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9 }}
        className="text-center"
      >
        <div className={`w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center ${
          isDark ? 'bg-purple-500/20' : 'bg-purple-100'
        }`}>
          <span className="text-2xl">⚡</span>
        </div>
        <a
          href="mailto:support@sweetpilot.com"
          className={`text-sm transition-colors ${
            isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'
          }`}
        >
          support@sweetpilot.com
        </a>
      </motion.div>
    </StaticPageLayout>
  );
}
